import { Collapse, List, ListItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { addDays, endOfToday, format, isBefore, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormikRangeDayPickerInput from '~components/date-picker/formik-range-day-picker-input';
import parseValueToDate from '~components/date-picker/helpers/parse-value-to-date';
import { DATE_FORMAT, SPLITTER } from '~components/date-picker/range-day-picker-input';
import FormikSelect from '~components/select/formik-select';
import { getTenancyOptionRangeDayPickerDisabledDays } from '~components/tenancy-options-select/get-tenancy-option-disabled-days';
import { onTenancyOptionsChange } from '~components/tenancy-options-select/on-tenancy-option-change';
import Typography from '~components/typography';
import {
  BEHAVIOR_TYPE,
  POINT_TYPE,
  TENANCY_LENGTH_OPTION,
  TENANCY_STATUS,
  TENANCY_TYPE,
} from '~graphql-types';
import { SettingsTermOptions } from '~svg-components';
import { TENANCY_CUSTOMIZED } from './constants';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },

  field: {
    marginTop: '20px',
    width: '320px',
    height: '48px',

    '&.Mui-error $fieldIcon': {
      color: theme.colors.red,
    },
  },

  fieldIcon: {
    marginLeft: '16px',
    color: theme.colors.grey66,
  },

  tenancyOptionSelect: {
    maxHeight: '300px',
    overflowX: 'scroll',

    '& > .Mui-selected $tenancyOptionName': {
      color: theme.colors.brandAction,
    },
  },

  tenancyOptionName: {},

  tenancyOptionDetail: {
    width: '320px',
    padding: '12px 20px',
    marginTop: '8px',
    borderRadius: theme.borderRadius.small,
    backgroundColor: theme.colors.greyF7,
  },

  tenancyOptionDetailItem: {
    ...theme.typography.body2,
    padding: 0,
    marginBottom: '8px',
    color: theme.colors.grey66,

    '&:last-child': {
      marginBottom: 0,
    },

    '&::before': {
      display: 'inline',
      content: "''",
      height: '4px',
      width: '4px',
      borderRadius: '50%',
      marginRight: '11px',
      backgroundColor: theme.colors.grey99,
    },
  },
  rollingTagWithNameRoot: {
    ...theme.flex.crossCenter,
    height: '24px',
  },
  rollingTagRoot: {
    ...theme.typography.caption,
    color: theme.colors.greenDark,
    padding: '1px 8px',
    borderRadius: '99px',
    backgroundColor: theme.colors.greenBg,
    marginLeft: '8px',
  },
}));

const formatDate = (date, str) => format(parseISO(date), str || 'iii d MMM yyyy');

const getFreeCancellationDate = (tenancyOption, dateRange) => {
  if (!tenancyOption || !dateRange) {
    return '';
  }
  const today = endOfToday();
  const { freeCancelInterval, freeCancelBehaviorType, freeCancelPointType } = tenancyOption;
  // This is the start date for either fixed or flexible tenancy option
  const tenancyStartDate = parseValueToDate(dateRange?.split(SPLITTER)[0], DATE_FORMAT);

  let freeCancellationDate;
  if (isBefore(tenancyStartDate, today)) {
    freeCancellationDate = today;
  } else {
    if (freeCancelPointType === POINT_TYPE.BOOKING_CONFIRMED_DATE) {
      freeCancellationDate = today;
    } else if (freeCancelPointType === POINT_TYPE.CONTRACT_START_DATE) {
      freeCancellationDate = tenancyStartDate;
    }
    freeCancellationDate = addDays(
      freeCancellationDate,
      freeCancelInterval * (freeCancelBehaviorType === BEHAVIOR_TYPE.BEFORE ? -1 : 1)
    );
    if (isBefore(tenancyStartDate, freeCancellationDate)) {
      freeCancellationDate = tenancyStartDate;
    }
  }
  return format(freeCancellationDate, DATE_FORMAT);
};

const getTenancyOptionList = (tenancyOptions, t, classes) => {
  if (!tenancyOptions) {
    return [];
  }
  return tenancyOptions
    .slice(0)
    .filter((item) => item.status === TENANCY_STATUS.ACTIVE)
    .sort((a, b) => {
      if (a === b || a.name === b.name) {
        return 0;
      }
      if (a.name < b.name) {
        return -1;
      }
      return 1;
    })
    .map((item) => ({
      value: item.id,
      children: (
        <div>
          <div className={classes.rollingTagWithNameRoot}>
            <Typography variant="dropdown" themeColor="dark" className={classes.tenancyOptionName}>
              {item.name}
            </Typography>
            {item?.tenancyLengthOption === TENANCY_LENGTH_OPTION.ROLLING && (
              <div className={classes.rollingTagRoot}>
                {t(`tenancy_length_option.${item?.tenancyLengthOption}`)}
              </div>
            )}
          </div>
          <Typography variant="body2" themeColor="grey99">
            {`${formatDate(item.startDate)} - ${formatDate(item.endDate)} (`}
            {t(`tenancy_length_type.${item.tenancyLengthType}`)}
            {` ${item.tenancyLength} ${t('tier_unit.NIGHTS')})`}
          </Typography>
        </div>
      ),
    }));
};

function BookingFormTenancy({
  loading = false,
  formik,
  tenancyOptions,
  currentTenancyOption,
  onClickCustomizedTenancyOption = () => {},
  setValuesWithAutoClearRef = { current: () => {} },
  hasCustomizeOption = true,
}) {
  const classes = useStyles();
  const { t } = useTranslation('booking');
  const { t: graphqlT } = useTranslation('graphql');
  const { values } = formik;

  const setTenancyOption = (value) => {
    setValuesWithAutoClearRef.current({
      tenancy_option: value,
    });
  };

  useEffect(() => {
    if (values.date_range && currentTenancyOption?.tenancyType === TENANCY_TYPE.FLEXIBLE) {
      const [startDate, endDate] = values.date_range.split(SPLITTER);
      const [from, to] = onTenancyOptionsChange({
        tenancyOption: currentTenancyOption,
        selectedStartDate: parseValueToDate(startDate, DATE_FORMAT),
        selectedEndDate: endDate ? parseValueToDate(endDate, DATE_FORMAT) : undefined,
      });
      setValuesWithAutoClearRef.current({
        date_range: `${format(from, DATE_FORMAT)}${SPLITTER}${to ? format(to, DATE_FORMAT) : ''}`,
      });
    }
  }, [currentTenancyOption, values.date_range, setValuesWithAutoClearRef]);

  useEffect(() => {
    if (currentTenancyOption?.tenancyType === TENANCY_TYPE.FIXED) {
      setValuesWithAutoClearRef.current({
        date_range: `${formatDate(
          currentTenancyOption.startDate,
          DATE_FORMAT
        )}${SPLITTER}${formatDate(currentTenancyOption.endDate, DATE_FORMAT)}`,
      });
    }
  }, [currentTenancyOption, setValuesWithAutoClearRef]);

  const disabledDays = useMemo(() => {
    if (currentTenancyOption?.tenancyType === TENANCY_TYPE.FLEXIBLE) {
      return getTenancyOptionRangeDayPickerDisabledDays({
        tenancyOption: currentTenancyOption,
        selectedStartDate: values.date_range && values.date_range.split(SPLITTER)[0],
        selectedEndDate: values.date_range && values.date_range.split(SPLITTER)[1],
      });
    }
    return [];
  }, [currentTenancyOption, values.date_range]);

  const tenancyOptionList = useMemo(
    () => getTenancyOptionList(tenancyOptions?.tenancyOptions, graphqlT, classes),
    [tenancyOptions, graphqlT, classes]
  );

  let freeCancellationDate;
  let intervalForTurnaround;
  let rateType;
  if (values.tenancy_option === TENANCY_CUSTOMIZED) {
    freeCancellationDate = values.free_cancellation_deadline_date;
    intervalForTurnaround = values.interval_for_turnaround;
    rateType = values.rate_type;
  } else {
    freeCancellationDate = getFreeCancellationDate(currentTenancyOption, values.date_range);
    intervalForTurnaround = currentTenancyOption?.intervalForTurnaround || 0;
    rateType = currentTenancyOption?.rateType || '';
  }

  const isFlexible = currentTenancyOption?.tenancyType === TENANCY_TYPE.FLEXIBLE;

  const renderRollingTag = () => (
    <div className={classes.rollingTagRoot}>
      {graphqlT(`tenancy_length_option.${TENANCY_LENGTH_OPTION.ROLLING}`)}
    </div>
  );

  return (
    <div className={classes.root}>
      <FormikSelect
        className={classes.field}
        placeholder={t('booking.creating.form.tenancy_option')}
        fieldConfig="tenancy_option"
        disabled={!values.academic_year || loading}
        loading={loading}
        options={tenancyOptionList}
        InputProps={{
          startAdornment: <SettingsTermOptions className={classes.fieldIcon} />,
        }}
        ListProps={{
          className: classes.tenancyOptionSelect,
        }}
        actionOptions={
          hasCustomizeOption && [
            {
              value: TENANCY_CUSTOMIZED,
              children: t('booking.creating.form.customize'),
              onClick: onClickCustomizedTenancyOption,
            },
          ]
        }
        renderValue={(value) => {
          if (value === TENANCY_CUSTOMIZED) {
            return t('booking.creating.form.customize');
          }
          const findTO = (tenancyOptions?.tenancyOptions || []).find((item) => item?.id === value);
          if (findTO) {
            return (
              <div className={classes.rollingTagWithNameRoot}>
                <Typography
                  variant="dropdown"
                  themeColor="dark"
                  className={classes.tenancyOptionName}
                >
                  {findTO?.name}
                </Typography>
                {findTO?.tenancyLengthOption === TENANCY_LENGTH_OPTION.ROLLING &&
                  renderRollingTag()}
              </div>
            );
          }
          return (
            <Typography variant="body1" themeColor="grey99">
              {t('booking.creating.form.tenancy_option')}
            </Typography>
          );
        }}
        onChange={(event) => {
          setTenancyOption(event.target.value);
        }}
      />

      <Collapse in={isFlexible} mountOnEnter unmountOnExit>
        <FormikRangeDayPickerInput
          className={classes.field}
          fieldConfig="date_range"
          InputProps={{
            iconPlacement: 'start',
            placeholder: t('booking.creating.form.date_range'),
          }}
          DayPickerProps={{
            disabledDays,
          }}
          canReverse={false}
        />
      </Collapse>

      <Collapse in={!!values.tenancy_option} mountOnEnter unmountOnExit>
        <List className={classes.tenancyOptionDetail}>
          {!isFlexible && values.date_range && (
            <ListItem className={classes.tenancyOptionDetailItem}>{values.date_range}</ListItem>
          )}
          {rateType && (
            <ListItem className={classes.tenancyOptionDetailItem}>{`${t('rate_type')} - ${graphqlT(
              `rate_type.${rateType}`
            )}`}</ListItem>
          )}
          {freeCancellationDate && (
            <ListItem className={classes.tenancyOptionDetailItem}>
              {t('booking.creating.tenancy_option.free_cancellation_date', {
                date: freeCancellationDate,
              })}
            </ListItem>
          )}
          <ListItem className={classes.tenancyOptionDetailItem}>
            {t('booking.creating.tenancy_option.interval_for_turnaround', {
              days: intervalForTurnaround,
            })}
          </ListItem>
        </List>
      </Collapse>
    </div>
  );
}

BookingFormTenancy.propTypes = {
  formik: PropTypes.object.isRequired,
  tenancyOptions: PropTypes.object,
  currentTenancyOption: PropTypes.object,
  loading: PropTypes.bool,
  setValuesWithAutoClearRef: PropTypes.object,
  onClickCustomizedTenancyOption: PropTypes.func,
  hasCustomizeOption: PropTypes.bool,
};

export default BookingFormTenancy;
